import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../routing/LanguageWrapper';

export function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const { lang } = useParams();
  
  // Get current language from URL or default
  const currentLang = lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F5F5F0',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #EFEFEF',
          borderTopColor: '#4A5A3A',
          borderRadius: '50%',
          animation: 'spin 800ms linear infinite',
        }} />
      </div>
    );
  }

  if (!isAuthenticated()) {
    // Redirect to language-prefixed login, preserving intended destination
    return <Navigate to={`/${currentLang}/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Check role-based access
  // SUPERADMIN has access to everything
  if (user?.role === 'superadmin') {
    return children;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Get current path without language prefix for checking
    const pathSegments = location.pathname.split('/').filter(Boolean);
    let currentPath = location.pathname;
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
      currentPath = '/' + pathSegments.slice(1).join('/');
    }
    
    // Redirect to dashboard if not authorized
    // If user is already on dashboard but doesn't have access (unlikely), redirect to login
    if (currentPath === '/dashboard' && !allowedRoles.includes(user?.role)) {
      return <Navigate to={`/${currentLang}/login`} replace />;
    }
    return <Navigate to={`/${currentLang}/dashboard`} replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};
